// TraitementDiversManager.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Search,
    Filter,
    Check,
    X,
    Calendar,
    Clock,
    AlertCircle,
    FileText,
    ChevronDown,
    Download,
    MessageSquare,
    Eye,
    User,
} from "lucide-react";

import "../TraitementStyles/TraitementDivers.css";

import {
    getManagerAttestationInboxRequest,
    managerAttestationDecisionRequest,
    downloadAttestationRequestPdfRequest,
    clearAttestationsMessage,
} from "../../../../redux/actions/attestationsActions";

const TraitementDiversManager = () => {
    const dispatch = useDispatch();

    // ====== STORE ======
    const managerInbox = useSelector((state) => state.attestations?.managerInbox || []);
    const loading = useSelector((state) => !!state.attestations?.loading);
    const apiError = useSelector((state) => state.attestations?.error || "");
    const apiMessage = useSelector((state) => state.attestations?.message || "");

    // ====== UI STATE ======
    const [activeTab, setActiveTab] = useState("enCours"); // enCours | traite
    const [searchTerm, setSearchTerm] = useState("");
    const [showFilters, setShowFilters] = useState(false);

    const [showRejectModal, setShowRejectModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedDemande, setSelectedDemande] = useState(null);
    const [motifRejet, setMotifRejet] = useState("");

    const [filters, setFilters] = useState({
        typeAttestation: "",
        departement: "",
        dateDebut: "",
        dateFin: "",
    });

    // ====== FETCH INBOX ======
    useEffect(() => {
        dispatch(getManagerAttestationInboxRequest());
    }, [dispatch]);

    // ====== CLEAR MESSAGES ======
    useEffect(() => {
        if (apiError || apiMessage) {
            const t = setTimeout(() => dispatch(clearAttestationsMessage()), 1500);
            return () => clearTimeout(t);
        }
    }, [apiError, apiMessage, dispatch]);

    // ====== HELPERS ======
    const formatDate = (d) => {
        if (!d) return "—";
        const dt = new Date(d);
        if (Number.isNaN(dt.getTime())) return String(d).slice(0, 10);
        return dt.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
    };

    const mapStatusToUi = (statusRaw) => {
        const s = String(statusRaw || "").toUpperCase();
        if (s === "PENDING_MANAGER") return "enCours";
        if (s === "PENDING_ADMIN" || s === "APPROVED") return "approuve";
        if (s === "REJECTED") return "rejete";
        return "enCours";
    };

    const getStatutBadge = (statut, statusRaw) => {
        const config = {
            enCours: { label: "En attente", bg: "#fff3cd", color: "#856404" },
            approuve: {
                label: statusRaw === "PENDING_ADMIN" ? "Approuvé (en cours RH)" : "Approuvé",
                bg: "#d4edda",
                color: "#155724"
            },
            rejete: { label: "Rejeté", bg: "#f8d7da", color: "#721c24" },
        };
        const c = config[statut] || config.enCours;
        return (
            <span className="tdBadge" style={{ backgroundColor: c.bg, color: c.color }}>
                {c.label}
            </span>
        );
    };

    // ====== MAP + FILTER + TAB ======
    const demandesMapped = useMemo(() => {
        const toStr = (v) => (v === null || v === undefined ? "" : String(v));

        const mapped = (managerInbox || []).map((d) => {
            const employee = d.employee || d.user || d.demandeur || {};
            const att = d.attestation || {};

            const statusRaw = toStr(d.status || d.statut).toUpperCase();
            const statut = mapStatusToUi(statusRaw);

            return {
                id: d.id,
                matricule: d.matricule || employee.matricule || employee.code || "—",
                collaborateur:
                    d.fullName ||
                    d.nomComplet ||
                    employee.fullName ||
                    employee.nomComplet ||
                    `${d.employeeNom || employee.nom || ""} ${d.employeePrenom || employee.prenom || ""}`.trim() ||
                    "—",
                poste: d.poste || d.jobTitle || employee.poste || employee.jobTitle || d.posteNom || employee.posteNom || "—",
                departement: d.departement || d.department || employee.departement || employee.department || d.departementNom || employee.departementNom || "—",

                typeAttestation: d.attestationTitre || att.titre || att.nom || "Attestation",
                couleurType: d.couleurType || att.couleur || "#3b82f6",

                dateDemande: d.createdAt || d.dateDemande || "",
                motif: d.motif || d.commentaire || "",

                // détails optionnels
                typeDomiciliation: d.typeDomiciliation || "",
                montantDomiciliation: d.montantDomiciliation,
                pourcentageDomiciliation: d.pourcentageDomiciliation,
                moisDemandes: d.moisDemandes || d.moisSelectionnes || null,

                documentsJoints: d.attachments || d.documentsJoints || [],
                managerComment: d.managerComment || "",
                adminComment: d.adminComment || "",

                statut,
                statusRaw,
                canDownload: statusRaw === "APPROVED",
            };
        });

        // search
        const s = searchTerm.trim().toLowerCase();
        let filtered = mapped;
        if (s) {
            filtered = filtered.filter((x) =>
                [
                    x.collaborateur,
                    x.matricule,
                    x.departement,
                    x.poste,
                    x.typeAttestation,
                    x.motif,
                ]
                    .join(" ")
                    .toLowerCase()
                    .includes(s)
            );
        }

        // filters
        if (filters.typeAttestation) filtered = filtered.filter((x) => x.typeAttestation === filters.typeAttestation);
        if (filters.departement) filtered = filtered.filter((x) => x.departement === filters.departement);
        if (filters.dateDebut) filtered = filtered.filter((x) => String(x.dateDemande).slice(0, 10) >= filters.dateDebut);
        if (filters.dateFin) filtered = filtered.filter((x) => String(x.dateDemande).slice(0, 10) <= filters.dateFin);

        // tab
        if (activeTab === "enCours") return filtered.filter((x) => x.statut === "enCours");
        if (activeTab === "traite") return filtered.filter((x) => x.statut === "approuve" || x.statut === "rejete");
        return filtered;
    }, [managerInbox, searchTerm, filters, activeTab]);

    // pour les <select> filtres
    const typesAttestation = useMemo(() => {
        return [...new Set((managerInbox || []).map((d) => d.attestationTitre || d.attestation?.titre || d.attestation?.nom).filter(Boolean))];
    }, [managerInbox]);

    const departements = useMemo(() => {
        return [
            ...new Set(
                (managerInbox || [])
                    .map((d) => {
                        const employee = d.employee || d.user || d.demandeur || {};
                        return d.departement || d.department || employee.departement || employee.department || d.departementNom || employee.departementNom;
                    })
                    .filter(Boolean)
            ),
        ];
    }, [managerInbox]);

    const tabs = useMemo(() => {
        const enCoursCount = (managerInbox || []).filter((d) => mapStatusToUi(d.status || d.statut) === "enCours").length;
        const traiteCount = (managerInbox || []).filter((d) => {
            const s = mapStatusToUi(d.status || d.statut);
            return s === "approuve" || s === "rejete";
        }).length;

        return [
            { id: "enCours", label: "En attente", count: enCoursCount },
            { id: "traite", label: "Traitées", count: traiteCount },
        ];
    }, [managerInbox]);

    // ====== ACTIONS ======
    const handleApprove = (demandeId) => {
        if (!demandeId) return;
        dispatch(managerAttestationDecisionRequest({ id: demandeId, approved: true, comment: "" }));
    };

    const handleReject = (demande) => {
        setSelectedDemande(demande);
        setMotifRejet("");
        setShowRejectModal(true);
    };

    const confirmReject = () => {
        if (!selectedDemande?.id) return;
        if (!motifRejet.trim()) return;

        const payload = { id: selectedDemande.id, approved: false, comment: motifRejet };
        dispatch(managerAttestationDecisionRequest(payload));

        setShowRejectModal(false);
        setMotifRejet("");
        setSelectedDemande(null);
    };

    const handleViewDetails = (demande) => {
        setSelectedDemande(demande);
        setShowDetailsModal(true);
    };

    const closeDetailsModal = () => {
        setShowDetailsModal(false);
        setSelectedDemande(null);
    };

    const closeRejectModal = () => {
        setShowRejectModal(false);
        setMotifRejet("");
        setSelectedDemande(null);
    };

    const handleDownload = (demande) => {
        if (!demande?.id) return;
        dispatch(downloadAttestationRequestPdfRequest({ requestId: demande.id, filename: `${demande.typeAttestation}.pdf` }));
    };

    return (
        <div className="tdPage">
            <div className="tdWrap">
                {/* Header */}
                <div className="tdHeader">
                    <div>
                        <h1>Traitement des demandes d'attestations</h1>
                        <p>Validation Manager - Vos collaborateurs</p>
                    </div>
                </div>

                {/* Search + Filters */}
                <div className="tdCard">
                    <div className="tdControls">
                        <div className="tdSearch">
                            <Search size={18} />
                            <input
                                type="text"
                                placeholder="Rechercher par matricule, nom, département, type d'attestation..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        <button className="tdFilterBtn" type="button" onClick={() => setShowFilters((v) => !v)}>
                            <Filter size={16} />
                            Filtres
                            <ChevronDown size={16} className={`tdChevron ${showFilters ? "is-open" : ""}`} />
                        </button>
                    </div>

                    {showFilters && (
                        <div className="tdFilters">
                            <div className="tdField">
                                <label>Type d'attestation</label>
                                <select
                                    value={filters.typeAttestation}
                                    onChange={(e) => setFilters((p) => ({ ...p, typeAttestation: e.target.value }))}
                                >
                                    <option value="">Tous</option>
                                    {typesAttestation.map((type) => (
                                        <option key={type} value={type}>
                                            {type}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="tdField">
                                <label>Département</label>
                                <select
                                    value={filters.departement}
                                    onChange={(e) => setFilters((p) => ({ ...p, departement: e.target.value }))}
                                >
                                    <option value="">Tous</option>
                                    {departements.map((dept) => (
                                        <option key={dept} value={dept}>
                                            {dept}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="tdField">
                                <label>Date demande (après)</label>
                                <input
                                    type="date"
                                    value={filters.dateDebut}
                                    onChange={(e) => setFilters((p) => ({ ...p, dateDebut: e.target.value }))}
                                />
                            </div>

                            <div className="tdField">
                                <label>Date demande (avant)</label>
                                <input
                                    type="date"
                                    value={filters.dateFin}
                                    onChange={(e) => setFilters((p) => ({ ...p, dateFin: e.target.value }))}
                                />
                            </div>
                        </div>
                    )}
                </div>

                {/* Tabs + Table */}
                <div className="tdCard">
                    <div className="tdTabs">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                type="button"
                                onClick={() => setActiveTab(tab.id)}
                                className={`tdTab ${activeTab === tab.id ? "is-active" : ""}`}
                            >
                                {tab.label}
                                <span className={`tdCount ${activeTab === tab.id ? "is-active" : ""}`}>{tab.count}</span>
                            </button>
                        ))}
                    </div>

                    <div className="tdTableWrap">
                        {loading && demandesMapped.length === 0 ? (
                            <div className="tdEmpty">
                                <Clock size={46} />
                                <div>Chargement...</div>
                            </div>
                        ) : demandesMapped.length === 0 ? (
                            <div className="tdEmpty">
                                <AlertCircle size={46} />
                                <div>Aucune demande trouvée</div>
                            </div>
                        ) : (
                            <table className="tdTable">
                                <thead>
                                <tr>
                                    <th>Matricule</th>
                                    <th>Collaborateur</th>
                                    <th>Département</th>
                                    <th>Type d'attestation</th>
                                    <th>Date demande</th>
                                    <th>Motif</th>
                                    <th>Statut</th>
                                    <th className="is-right">Actions</th>
                                </tr>
                                </thead>

                                <tbody>
                                {demandesMapped.map((demande) => (
                                    <tr key={demande.id}>
                                        <td>
                                            <span className="tdPrimary">{demande.matricule}</span>
                                        </td>

                                        <td>
                                            <div className="tdName">
                                                <span className="tdPrimary">{demande.collaborateur}</span>
                                            </div>
                                            <div className="tdSub">{demande.poste}</div>
                                        </td>

                                        <td>
                                            <span className="tdMuted">{demande.departement}</span>
                                        </td>

                                        <td>
                                            <div className="tdType">
                                                <span className="tdDot" style={{ backgroundColor: demande.couleurType }} />
                                                <span className="tdPrimary">{demande.typeAttestation}</span>
                                            </div>
                                        </td>

                                        <td>
                                            <div className="tdDate">
                                                <Calendar size={14} />
                                                <span className="tdMuted">{formatDate(demande.dateDemande)}</span>
                                            </div>
                                        </td>

                                        <td>
                                            <div className="tdTrunc" title={demande.motif}>
                                                {demande.motif || "—"}
                                            </div>
                                        </td>

                                        <td>{getStatutBadge(demande.statut, demande.statusRaw)}</td>

                                        <td className="is-right">
                                            <div className="tdRowActions">
                                                <button
                                                    type="button"
                                                    className="tdIconBtn"
                                                    onClick={() => handleViewDetails(demande)}
                                                    title="Voir les détails"
                                                >
                                                    <Eye size={16} />
                                                </button>

                                                {demande.canDownload && (
                                                    <button
                                                        type="button"
                                                        className="tdActionBtn tdActionBtn--dl"
                                                        title="Télécharger"
                                                        onClick={() => handleDownload(demande)}
                                                    >
                                                        <Download size={16} />
                                                    </button>
                                                )}

                                                {activeTab === "enCours" && (
                                                    <>
                                                        <button
                                                            type="button"
                                                            className="tdActionBtn tdActionBtn--ok"
                                                            onClick={() => handleApprove(demande.id)}
                                                            title="Approuver"
                                                        >
                                                            <Check size={16} />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="tdActionBtn tdActionBtn--no"
                                                            onClick={() => handleReject(demande)}
                                                            title="Refuser"
                                                        >
                                                            <X size={16} />
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>

            {/* Details Modal */}
            {showDetailsModal && selectedDemande && (
                <div className="tdModal">
                    <div className="tdModal__panel" role="dialog" aria-modal="true">
                        <div className="tdModal__header">
                            <div className="tdModal__headLeft">
                                <div className="tdModal__icon">
                                    <FileText size={18} />
                                </div>
                                <div>
                                    <div className="tdModal__title">Détails de la demande</div>
                                    <div className="tdModal__sub">{selectedDemande.typeAttestation}</div>
                                </div>
                            </div>

                            <button type="button" className="tdIconBtn" onClick={closeDetailsModal} aria-label="Fermer">
                                <X size={18} />
                            </button>
                        </div>

                        <div className="tdModal__body">
                            {/* Collaborateur */}
                            <div className="tdPanel">
                                <div className="tdPanel__title">Informations du collaborateur</div>
                                <div className="tdGrid2">
                                    <div className="tdInfo">
                                        <div className="tdInfo__label">Matricule</div>
                                        <div className="tdInfo__value">{selectedDemande.matricule}</div>
                                    </div>
                                    <div className="tdInfo">
                                        <div className="tdInfo__label">Nom complet</div>
                                        <div className="tdInfo__value">{selectedDemande.collaborateur}</div>
                                    </div>
                                    <div className="tdInfo">
                                        <div className="tdInfo__label">Poste</div>
                                        <div className="tdInfo__value">{selectedDemande.poste}</div>
                                    </div>
                                    <div className="tdInfo">
                                        <div className="tdInfo__label">Département</div>
                                        <div className="tdInfo__value">{selectedDemande.departement}</div>
                                    </div>
                                </div>
                            </div>

                            {/* Détails demande */}
                            <div className="tdBlock">
                                <div className="tdBlock__title">Détails de la demande</div>

                                <div className="tdLine">
                                    <FileText size={16} />
                                    <div className="tdLine__content">
                                        <div className="tdLine__label">Type d'attestation</div>
                                        <div className="tdLine__value">{selectedDemande.typeAttestation}</div>
                                    </div>
                                </div>

                                <div className="tdLine">
                                    <Calendar size={16} />
                                    <div className="tdLine__content">
                                        <div className="tdLine__label">Date de demande</div>
                                        <div className="tdLine__value">{formatDate(selectedDemande.dateDemande)}</div>
                                    </div>
                                </div>

                                <div className="tdLine">
                                    <MessageSquare size={16} />
                                    <div className="tdLine__content">
                                        <div className="tdLine__label">Motif de la demande</div>
                                        <div className="tdLine__value">{selectedDemande.motif || "—"}</div>
                                    </div>
                                </div>

                                {/* Documents */}
                                {Array.isArray(selectedDemande.documentsJoints) && selectedDemande.documentsJoints.length > 0 && (
                                    <div className="tdLine">
                                        <Download size={16} />
                                        <div className="tdLine__content">
                                            <div className="tdLine__label">Documents joints</div>
                                            <div className="tdDocs">
                                                {selectedDemande.documentsJoints.map((doc, idx) => (
                                                    <button
                                                        type="button"
                                                        key={idx}
                                                        className="tdDoc"
                                                        title="Télécharger"
                                                    >
                                                        <Download size={14} />
                                                        <span>{typeof doc === "string" ? doc : doc?.name || "Document"}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Validation info */}
                            {(selectedDemande.statut === "approuve" || selectedDemande.statut === "rejete") && (
                                <div className={`tdValidation ${selectedDemande.statut === "approuve" ? "is-ok" : "is-no"}`}>
                                    <div className="tdValidation__title">Informations de validation Manager</div>

                                    <div className="tdValidation__row">
                                        <span className="tdMuted">Statut :</span>
                                        {getStatutBadge(selectedDemande.statut, selectedDemande.statusRaw)}
                                    </div>

                                    {selectedDemande.managerComment && (
                                        <div className="tdRejectBox">
                                            <div className="tdRejectBox__label">Commentaire Manager :</div>
                                            <div className="tdRejectBox__value">
                                                {selectedDemande.managerComment}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Footer actions */}
                        {activeTab === "enCours" && (
                            <div className="tdModal__footer">
                                <button
                                    type="button"
                                    className="tdBtn tdBtn--ghost"
                                    onClick={() => {
                                        closeDetailsModal();
                                        handleReject(selectedDemande);
                                    }}
                                >
                                    <X size={18} />
                                    Refuser
                                </button>

                                <button
                                    type="button"
                                    className="tdBtn tdBtn--ok"
                                    onClick={() => {
                                        handleApprove(selectedDemande.id);
                                        closeDetailsModal();
                                    }}
                                >
                                    <Check size={18} />
                                    Approuver
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Reject Modal */}
            {showRejectModal && selectedDemande && (
                <div className="tdModal">
                    <div className="tdModal__panel tdModal__panel--small" role="dialog" aria-modal="true">
                        <div className="tdModal__header">
                            <div className="tdModal__headLeft">
                                <div className="tdModal__icon tdModal__icon--danger">
                                    <AlertCircle size={18} />
                                </div>
                                <div>
                                    <div className="tdModal__title">Refuser la demande d'attestation</div>
                                    <div className="tdModal__sub">
                                        {selectedDemande.collaborateur} — {selectedDemande.typeAttestation}
                                    </div>
                                </div>
                            </div>

                            <button type="button" className="tdIconBtn" onClick={closeRejectModal} aria-label="Fermer">
                                <X size={18} />
                            </button>
                        </div>

                        <div className="tdModal__body">
                            <label className="tdLabel">
                                Motif du rejet <span className="tdReq">*</span>
                            </label>
                            <textarea
                                value={motifRejet}
                                onChange={(e) => setMotifRejet(e.target.value)}
                                rows={5}
                                placeholder="Veuillez préciser le motif du rejet de cette demande..."
                                className="tdTextarea"
                            />
                            <div className="tdHelp">Ce motif sera communiqué au collaborateur</div>
                        </div>

                        <div className="tdModal__footer">
                            <button type="button" className="tdBtn tdBtn--ghost" onClick={closeRejectModal}>
                                Annuler
                            </button>
                            <button
                                type="button"
                                className="tdBtn tdBtn--danger"
                                onClick={confirmReject}
                                disabled={!motifRejet.trim()}
                            >
                                Confirmer le rejet
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TraitementDiversManager;